import React, { useState, useEffect } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Grid, InputAdornment, Chip, Pagination, Stack, Switch,
    CircularProgress, Alert, Tooltip
} from '@mui/material';
import { FaSearch as SearchIcon } from 'react-icons/fa';
import { MdDeleteForever, MdAdd, MdEdit } from 'react-icons/md';
import { TbBrandAmazon } from 'react-icons/tb';
import { getData, postData, putData, deleteData, postFileUpload } from '../../Webservice/Webservice';
import { API_URL } from '../../Utils/url';
const IMAGE_BASE_URL = API_URL.replace('/api/', '');

const getToken = () => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    return token ? `Bearer ${token}` : '';
};

const PAGE_SIZE = 20;

const emptyForm = { name: '', description: '', isActive: true, imageFile: null, imagePreview: null, sortBy: 0 };

function BrandManagement() {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

    // Modals
    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [seedOpen, setSeedOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [seedResult, setSeedResult] = useState(null);
    const [error, setError] = useState('');

    const fetchBrands = async (p = page) => {
        setLoading(true);
        try {
            const res = await getData('brand/all', { page: p, limit: PAGE_SIZE, search: searchTerm }, getToken(), false);
            if (res?.success) {
                setBrands(res.brands || []);
                setMeta({ total: res.meta?.total || 0, totalPages: res.meta?.totalPages || 1 });
            }
        } catch (e) {
            console.error('Fetch brands failed:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchBrands(page); }, [page, searchTerm]);

    const handleAdd = async () => {
        setError('');
        if (!formData.name.trim()) { setError('Brand name is required.'); return; }
        setSaving(true);
        try {
            const submitData = new FormData();
            submitData.append('name', formData.name);
            submitData.append('description', formData.description || '');
            submitData.append('isActive', formData.isActive);
            if (formData.sortBy !== undefined) submitData.append('sortBy', formData.sortBy);
            if (formData.imageFile) submitData.append('image', formData.imageFile);

            await postFileUpload('brand/create', submitData, getToken(), false);
            setAddOpen(false);
            setFormData(emptyForm);
            fetchBrands(1);
        } catch (e) {
            setError(e.message || 'Failed to create brand');
        } finally { setSaving(false); }
    };

    const handleEdit = async () => {
        setError('');
        if (!formData.name.trim()) { setError('Brand name is required.'); return; }
        setSaving(true);
        try {
            const submitData = new FormData();
            submitData.append('name', formData.name);
            submitData.append('description', formData.description || '');
            submitData.append('isActive', formData.isActive);
            if (formData.sortBy !== undefined) submitData.append('sortBy', formData.sortBy);
            if (formData.imageFile) submitData.append('image', formData.imageFile);

            await putData(`brand/update/${selected._id}`, submitData, getToken(), false);
            setEditOpen(false);
            setSelected(null);
            setFormData(emptyForm);
            fetchBrands(page);
        } catch (e) {
            setError(e.message || 'Failed to update brand');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await deleteData(`brand/delete/${selected._id}`, {}, getToken());
            setDeleteOpen(false);
            setSelected(null);
            fetchBrands(page);
        } catch (e) {
            console.error('Delete failed:', e);
        } finally { setSaving(false); }
    };

    const handleToggleActive = async (brand) => {
        try {
            await putData(`brand/update/${brand._id}`, { isActive: !brand.isActive }, getToken(), false);
            fetchBrands(page);
        } catch (e) { console.error('Toggle failed:', e); }
    };

    const handleSeed = async () => {
        setSaving(true);
        setSeedResult(null);
        try {
            const res = await postData('brand/seed', {}, getToken(), false, false);
            setSeedResult(res);
            fetchBrands(1);
        } catch (e) {
            setSeedResult({ success: false, message: e.message });
        } finally { setSaving(false); }
    };

    const openEditModal = (brand) => {
        setSelected(brand);
        setFormData({ 
            name: brand.name, 
            description: brand.description || '', 
            isActive: brand.isActive,
            sortBy: brand.sortBy || 0,
            imageFile: null,
            imagePreview: brand.image ? `${IMAGE_BASE_URL}${brand.image}` : null
        });
        setError('');
        setEditOpen(true);
    };

    const openDeleteModal = (brand) => {
        setSelected(brand);
        setDeleteOpen(true);
    };

    const th = {
        fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: '0.72rem',
        textTransform: 'uppercase', letterSpacing: '0.05em', color: '#5c636a',
        backgroundColor: '#f8f9fa', borderBottom: '2px solid #edeff1', py: 2
    };

    return (
        <Box p={2} sx={{ background: '#f8fafc', borderRadius: '10px', minHeight: '100%' }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h3" component="h1" sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'Mulish, sans-serif' }}>
                    Brand Management
                    <Chip
                        label={`${meta.total} Brand${meta.total !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{ backgroundColor: 'rgba(255,209,2,0.2)', color: '#b39200', fontFamily: 'Mulish, sans-serif', fontWeight: 800, border: '1px solid rgba(255,209,2,0.5)' }}
                    />
                </Typography>

                <Stack direction="row" spacing={1.5}>
                    <Tooltip title="Import all brand names currently saved on products into master">
                        <Button
                            variant="outlined"
                            onClick={() => { setSeedResult(null); setSeedOpen(true); }}
                            sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none', borderRadius: '10px', fontWeight: 700, borderColor: '#C8A054', color: '#C8A054', '&:hover': { borderColor: '#b38a3a', bgcolor: 'rgba(200,160,84,0.07)' } }}
                        >
                            Import from Products
                        </Button>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<MdAdd />}
                        onClick={() => { setFormData(emptyForm); setError(''); setAddOpen(true); }}
                        sx={{ fontFamily: 'Mulish, sans-serif', background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255,209,2,0.3)', border: '1px solid black', '&:hover': { background: 'rgb(240,195,0)', transform: 'translateY(-1px)' } }}
                    >
                        Add Brand
                    </Button>
                </Stack>
            </Box>

            {/* Search */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth variant="outlined" placeholder="Search Brands..." size="small"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>), sx: { borderRadius: 2 } }}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #d3d3d3' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={th}>#</TableCell>
                            <TableCell sx={th}>Brand Name</TableCell>
                            <TableCell sx={th}>Description</TableCell>
                            <TableCell sx={th} align="center">Sort By</TableCell>
                            <TableCell align="center" sx={th}>Status</TableCell>
                            <TableCell align="right" sx={th}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                        ) : brands.length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary', fontFamily: 'Mulish, sans-serif' }}>No brands found. Click <strong>"Import from Products"</strong> to seed from existing product data.</TableCell></TableRow>
                        ) : brands.map((brand, idx) => (
                            <TableRow key={brand._id} hover sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                                <TableCell sx={{ fontFamily: 'Mulish, sans-serif', color: '#6b7280', fontSize: '0.85rem' }}>
                                    {(page - 1) * PAGE_SIZE + idx + 1}
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {brand.image ? (
                                            <Box component="img" src={`${IMAGE_BASE_URL}${brand.image}`} alt={brand.name} sx={{ width: 36, height: 36, borderRadius: '8px', objectFit: 'cover' }} />
                                        ) : (
                                            <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(255,209,2,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '14px', color: '#b39200', fontFamily: 'Mulish, sans-serif' }}>
                                                {brand.name.charAt(0).toUpperCase()}
                                            </Box>
                                        )}
                                        <Typography fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.95rem', color: '#1a1c1e' }}>
                                            {brand.name}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'Mulish, sans-serif', color: '#6b7280', fontSize: '0.88rem' }}>
                                    {brand.description || <span style={{ color: '#ccc', fontStyle: 'italic' }}>No description</span>}
                                </TableCell>
                                <TableCell align="center" sx={{ fontFamily: 'Mulish, sans-serif', color: '#6b7280', fontSize: '0.88rem' }}>
                                    {brand.sortBy || 0}
                                </TableCell>
                                <TableCell align="center">
                                    <Switch size="small" checked={!!brand.isActive} onChange={() => handleToggleActive(brand)} color="primary" />
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => openEditModal(brand)}
                                                sx={{ color: '#4682B4', bgcolor: 'rgba(70,130,180,0.1)', '&:hover': { bgcolor: 'rgba(70,130,180,0.2)' }, borderRadius: '8px' }}>
                                                <MdEdit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => openDeleteModal(brand)}
                                                sx={{ color: '#dc2626', bgcolor: 'rgba(220,38,38,0.1)', '&:hover': { bgcolor: 'rgba(220,38,38,0.2)' }, borderRadius: '8px' }}>
                                                <MdDeleteForever size={20} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                    count={meta.totalPages} page={page} onChange={(_, v) => setPage(v)} shape="rounded" showFirstButton showLastButton
                    sx={{ '& .MuiPaginationItem-root': { fontFamily: 'Mulish, sans-serif', fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: 'rgb(255,209,2)', color: 'black' } } }}
                />
            </Box>

            {/* ── Add Modal ── */}
            <Dialog open={addOpen} onClose={() => setAddOpen(false)} PaperProps={{ sx: { borderRadius: '12px', p: 1, minWidth: 420 } }}>
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800 }}>Add New Brand</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField autoFocus fullWidth label="Brand Name *" variant="outlined" size="small" value={formData.name}
                                onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} sx={{ mb: 2, mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Sort By" type="number" variant="outlined" size="small" value={formData.sortBy}
                                onChange={(e) => setFormData(f => ({ ...f, sortBy: e.target.value }))} sx={{ mb: 2, mt: 1 }} />
                        </Grid>
                    </Grid>
                    <TextField fullWidth label="Description" variant="outlined" size="small" multiline rows={3} value={formData.description}
                        onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))} sx={{ mb: 2 }} />
                    <Button variant="outlined" component="label" sx={{ mb: 2, textTransform: 'none', borderRadius: '8px', fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>
                        Upload Brand Image
                        <input type="file" hidden accept="image/*" onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                                setFormData(f => ({ ...f, imageFile: e.target.files[0], imagePreview: URL.createObjectURL(e.target.files[0]) }));
                            }
                        }} />
                    </Button>
                    {formData.imagePreview && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                            <img src={formData.imagePreview} alt="Preview" style={{ width: 80, height: 80, borderRadius: '8px', objectFit: 'cover' }} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setAddOpen(false)} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleAdd} variant="contained" disabled={saving}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: 'rgb(255,209,2)', color: 'black', '&:hover': { bgcolor: 'rgb(240,195,0)' } }}>
                        {saving ? <CircularProgress size={18} /> : 'Add Brand'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit Modal ── */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} PaperProps={{ sx: { borderRadius: '12px', p: 1, minWidth: 420 } }}>
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800 }}>Edit Brand</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField autoFocus fullWidth label="Brand Name *" variant="outlined" size="small" value={formData.name}
                                onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} sx={{ mb: 2, mt: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Sort By" type="number" variant="outlined" size="small" value={formData.sortBy}
                                onChange={(e) => setFormData(f => ({ ...f, sortBy: e.target.value }))} sx={{ mb: 2, mt: 1 }} />
                        </Grid>
                    </Grid>
                    <TextField fullWidth label="Description" variant="outlined" size="small" multiline rows={3} value={formData.description}
                        onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))} sx={{ mb: 2 }} />
                    <Button variant="outlined" component="label" sx={{ mb: 2, textTransform: 'none', borderRadius: '8px', fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>
                        Upload Brand Image
                        <input type="file" hidden accept="image/*" onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                                setFormData(f => ({ ...f, imageFile: e.target.files[0], imagePreview: URL.createObjectURL(e.target.files[0]) }));
                            }
                        }} />
                    </Button>
                    {formData.imagePreview && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                            <img src={formData.imagePreview} alt="Preview" style={{ width: 80, height: 80, borderRadius: '8px', objectFit: 'cover' }} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEditOpen(false)} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleEdit} variant="contained" disabled={saving}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: 'rgb(255,209,2)', color: 'black', '&:hover': { bgcolor: 'rgb(240,195,0)' } }}>
                        {saving ? <CircularProgress size={18} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Modal ── */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} PaperProps={{ sx: { borderRadius: '12px', p: 1, minWidth: 380 } }}>
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#dc2626' }}>Delete Brand</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: 'Mulish, sans-serif' }}>
                        Are you sure you want to delete <strong>{selected?.name}</strong>? This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteOpen(false)} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" disabled={saving}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {saving ? <CircularProgress size={18} /> : 'Delete Brand'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Seed Modal ── */}
            <Dialog open={seedOpen} onClose={() => setSeedOpen(false)} PaperProps={{ sx: { borderRadius: '12px', p: 1, minWidth: 420 } }}>
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800 }}>Import Brands from Products</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: 'Mulish, sans-serif', color: '#374151', mb: 2 }}>
                        This will scan all existing products, extract unique brand names, and add any that are not already in Brand master. Existing entries won't be overwritten.
                    </Typography>
                    {seedResult && (
                        <Alert severity={seedResult.success ? 'success' : 'error'} sx={{ mt: 1 }}>
                            {seedResult.message}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setSeedOpen(false)} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>Close</Button>
                    <Button onClick={handleSeed} variant="contained" disabled={saving}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: '#C8A054', color: '#000', '&:hover': { bgcolor: '#b38a3a' } }}>
                        {saving ? <CircularProgress size={18} /> : 'Run Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default BrandManagement;
